import React, { FC, useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { Classification, ResponseData } from "../../../../../models";
import { IncomingEmailAddressRule, IncomingEmailAddressRuleRequest } from "../../../../../models/incomingEmailAddressRule";
import { getACDs, getAllClassifications } from "../../../../../services/get.data.service";
import SelectInput from "../../shared/fields/selectInput";
import TextInput from "../../shared/fields/textInput";
import ToggleInput from "../../shared/fields/toggleInput";
import SettingsFormInfo from "../../shared/settingsFormInfo";
import SettingsTitle from "../../shared/settingsTitle";
import { SelectOption } from "../../../../../models/selectOption";
import { putSetting } from "../../../../../services/put.data.service";
import { postSetting } from "../../../../../services/post.data.service";
import SettingsSubmitButton from "../../shared/settingsSubmitButton";

type Props = {
  isDuplicate: (fieldToCheck: keyof IncomingEmailAddressRule, value: string)=> boolean;
  existingSystemAccount?: IncomingEmailAddressRule;
  load: ()=>void;
};

const SystemAccountForm: FC<Props> = ({ isDuplicate, existingSystemAccount, load }) => {
  const [redirect, setRedirect] = useState<boolean>(false);

  const [classificationOptions, setClassificationOptions] = useState<SelectOption[]>([]);
  const [clientOptions, setClientOptions] = useState<SelectOption[]>([]);

  const [emailAddress, setEmailAddress] = useState<string>("");
  const [subject, setSubject] = useState<string>("");
  const [suppressAcknowledgement, setSuppressAcknowledgement] = useState<boolean>(false);
  const [classificationId, setClassificationId] = useState<number|null>(null);
  const [autoClose, setAutoClose] = useState<boolean>(false);
  const [clientId, setClientId] = useState<number|null>(null);

  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (existingSystemAccount) {
      setEmailAddress(existingSystemAccount.emailAddress || "");
      setSubject(existingSystemAccount.subject || "")
      setClassificationId(existingSystemAccount.classificationId || null);
      setSuppressAcknowledgement(existingSystemAccount.suppressAutoResponse);
      setAutoClose(existingSystemAccount.autoClose);
      setClientId(existingSystemAccount.clientId || null);
    }
    getACDs().then((clients) => {
      setClientOptions(
        clients.data.map((client) => {
          return {
            id: client.clientId,
            label: client.clientName,
          };
        })
      );
    });
    getAllClassifications().then((response) =>{
      setClassificationOptions(response.data.map((classification: Classification): SelectOption =>{
        return {
          id: classification.classificationId,
          label: classification.name
        }
      }));
    })
  }, [existingSystemAccount]);

  const validate = (): string[] => {
    const errorMessages: string[] = [];
    if (emailAddress.length === 0) {
      errorMessages.push("Email Address is required");
    } else {
      if (isDuplicate("emailAddress", emailAddress + subject.trim())) {
        errorMessages.push("Email Address / Subject combination has already been used");
      }
    }
    if (autoClose && clientId === null) {
      errorMessages.push("Client is required while Auto Close is selected");
    }
    return errorMessages;
  };
  
  const handleSaveResponse = (result: ResponseData<IncomingEmailAddressRuleRequest>): void => {
    if (result.success) {
      load(); 
      setRedirect(true);
    } else {
      setErrors([`Failed to update. ${result.message || result}`]);
    }
  };

  const save = async (): Promise<void> => {
    const errorMessages = validate();
    setErrors(errorMessages);
    if (!errorMessages.length) {
      // 
      const toSave: IncomingEmailAddressRuleRequest = {
        emailAddress,
        subject: subject.trim(),
        classificationId,
        clientId,
        suppressAutoResponse: suppressAcknowledgement,
        autoClose
      };
      if (existingSystemAccount) {
        return putSetting<IncomingEmailAddressRuleRequest>("incomingemailaccountrules", existingSystemAccount.incomingEmailAccountRuleId, toSave)
        .then((result: ResponseData<IncomingEmailAddressRuleRequest>) => {
          handleSaveResponse(result);
        });
      } else {
        return postSetting<IncomingEmailAddressRuleRequest>("incomingemailaccountrules", toSave)
        .then((result: ResponseData<IncomingEmailAddressRuleRequest>) => {
          handleSaveResponse(result);
        });
      }
    }
  };
  
  if (redirect) return <Redirect to="/settings/systemAccounts/list" />;

  return (
    <>
      <SettingsTitle errors={errors} title={existingSystemAccount ? "Edit SystemAccount" : "Create SystemAccount"}>
        <SettingsSubmitButton onSave={save} />
      </SettingsTitle>
      <div className="row">
        <SettingsFormInfo existingTitle={existingSystemAccount?.emailAddress} >
        </SettingsFormInfo>
        <div className="col-sm-7">
          <div className="content-container p-3">
            <TextInput 
              label="Email Address" 
              name="email-account" 
              onChange={setEmailAddress} 
              value={emailAddress} 
            />
            <TextInput 
              label="Subject" 
              name="subject" 
              onChange={setSubject} 
              value={subject} 
            />
            <ToggleInput
              label="Suppress ACK"
              name="suppress-ack"
              onChange={setSuppressAcknowledgement}
              value={suppressAcknowledgement}
            />
            <SelectInput
              label="Classification"
              name="classification"
              onChange={setClassificationId}
              id={classificationId}
              options={classificationOptions}
            />
            <ToggleInput
              label="Auto Close"
              name="auto-close"
              onChange={setAutoClose}
              value={autoClose}
            />
            <SelectInput
              label="Client"
              name="client"
              onChange={setClientId}
              id={clientId||null}
              options={clientOptions}
            />
          </div>
        </div>
      </div>
    </>
  );
};
export default SystemAccountForm;
